// Curated catalog of sub-links shown under each search hit in the global
// search autocomplete. Structure:
//
//   MODULE_LINKS[entityType][moduleName] = [ { key, menuItemName, path, subGroup? }, ... ]
//   MODULE_ORDER[entityType]              = ordered list of module names to render
//
// Permissions are checked against the user's access tree in Redux
// (state.access.access). A sub-link only renders if the user has
// menuAccess !== 0 for the matching menuItemName (trimmed) under the
// module's moduleorder. See buildPermissionIndex in StudentSearchResult.
//
// Routes here must match entries registered in src/route/routeConfig.js.
// Navigation goes through /deeplink/{entityType}/{entityId}/go{path} so the
// gate can hydrate the entity into Redux + history state before the legacy
// destination page mounts.

export const MODULE_ORDER = {
    student: ['Admissions', 'Registration', 'Billing', 'Financial Aid', 'Student Life'],
    applicant: ['Admissions'],
    faculty: ['CAMS Manager'],
    prospect: ['Admissions'],
    organization: ['Admissions'],
    contact: ['Admissions'],
    mealplan: ['Student Life'],
    housingperiod: ['Student Life'],
    property: ['Student Life'],
    user: ['CAMS Manager'],
    glossarytable: ['CAMS Manager'],
    referencetable: ['CAMS Manager']
};

// Display-only label overrides per entityType. The module name stays the
// permission/lookup key (see buildPermissionIndex); only the shown label changes.
export const MODULE_DISPLAY_NAMES = {
    student: { Admissions: 'Admissions > Students' },
    prospect: { Admissions: 'Admissions > Prospects' },
    contact: { Admissions: 'Admissions > Contacts' },
    organization: { Admissions: 'Admissions > Organizations' },
    // WI 303833 — the ticket labels the destination "Student Life > Housing >
    // Meal Plans" (legacy CAMS placement) even though the Elements nav reaches
    // it as Student Life > Meal Plans; the label follows the ticket, the route
    // goes to the real page.
    mealplan: { 'Student Life': 'Student Life > Housing > Meal Plans' },
    // WI 303823 — the ticket labels the destination "Student Life > Housing >
    // Housing Period"; that matches the Elements nav (Housing Periods lives
    // under Student Life > Housing).
    housingperiod: { 'Student Life': 'Student Life > Housing > Housing Period' },
    // WI 303828 — the ticket labels the destination "Student Life > Housing >
    // Properties"; that matches the Elements nav (Properties lives under
    // Student Life > Housing).
    property: { 'Student Life': 'Student Life > Housing > Properties' },
    // WI 303847 — the ticket labels the destination "System Manager"; the
    // permission/lookup key stays "CAMS Manager" because that is the module
    // name the access tree emits for moduleorder 13.
    faculty: { 'CAMS Manager': 'System Manager' },
    // WI 303843 — the ticket labels the destination "System Manager > Users > User
    // Administration". The permission/lookup key stays "CAMS Manager" for the same
    // reason as faculty: that is the module name the access tree emits for moduleorder 13.
    user: { 'CAMS Manager': 'System Manager > Users > User Administration' },
    // WI 305242 — both halves of Table Maintenance carry the ticket's label
    // "System Manager > Lookup Table Options > Table Maintenance". The
    // permission/lookup key stays "CAMS Manager" for the same reason as faculty
    // and user: that is the module name the access tree emits for moduleorder 13.
    glossarytable: { 'CAMS Manager': 'System Manager > Lookup Table Options > Table Maintenance' },
    referencetable: { 'CAMS Manager': 'System Manager > Lookup Table Options > Table Maintenance' }
};

const STUDENT_LINKS = {
    Admissions: [
        { key: 'Students', menuItemName: 'Students', path: '/admissions/students/add-students' },
        { key: 'ChangeAddress', menuItemName: 'Change Address', path: '/admission/students/change-address' },
        { key: 'StudentStatus', menuItemName: 'Student Status', path: '/admissions/students/student-status' },
        { key: 'StudentAccess', menuItemName: 'Student Access', path: '/admissions/students/student-access' },
        { key: 'StudentNotes', menuItemName: 'Student Notes', path: '/admissions/students/student-notes' },
        { key: 'DocumentTracking', menuItemName: 'Document Tracking', path: '/admissions/students/document-tracking' },
        { key: 'EvaluationsApplicant', menuItemName: 'Applicant', displayName: 'Evaluations > Applicant', path: '/admissions/students/evaluations/applicant' },
        { key: 'Sevis', menuItemName: 'Student SEVIS', path: '/admissions/students/student-sevis' },
        { key: 'LaptopTracking', menuItemName: 'Laptop Tracking', path: '/admissions/students/laptop-tracking' },
        { key: 'StudentRisk', menuItemName: 'Student Risk', path: '/admissions/students/student-risk' }
    ],
    Registration: [
        { key: 'Register', menuItemName: 'Register', path: '/registration/academic/register/register-parameters', subGroup: 'Academics' },
        { key: 'Unofficial', menuItemName: 'Unofficial', path: '/registration/academic/unofficial/parameters', subGroup: 'Academics' },
        { key: 'Transfer', menuItemName: 'Transfers', path: '/registration/academic/transfer', subGroup: 'Academics' },
        { key: 'StudentHolds', menuItemName: 'Holds', path: '/registration/academic/holds', subGroup: 'Academics' },
        { key: 'DocumentTracking', menuItemName: 'Document Tracking', path: '/registration/academic/document-tracking', subGroup: 'Academics' },
        { key: 'StudentNotes', menuItemName: 'Student Notes', path: '/registration/academic/student-notes', subGroup: 'Academics' },
        { key: 'StudentStatus', menuItemName: 'Status', displayName: 'Student Status', path: '/registration/academic/student-status', subGroup: 'Academics' },
        { key: 'ChangeAddress', menuItemName: 'Change Address', path: '/registration/academic/change-address', subGroup: 'Academics' },
        { key: 'Degrees', menuItemName: 'Degrees', path: '/registration/academic/degrees', subGroup: 'Academics' },
        { key: 'TranscriptMaintenance', menuItemName: 'Maintenance', path: '/registration/academic/transcript/maintenance', subGroup: 'Academics' },
        { key: 'AdvisorTranscript', menuItemName: 'Advisor', path: '/registration/academic/transcript/advisor', subGroup: 'Academics' },
        { key: 'StudentEvaluation', menuItemName: 'Student Evaluation', path: '/registration/degree-audit/student-evaluation', subGroup: 'Degree Audit' }
    ],
    Billing: [
        { key: 'Maintenance', menuItemName: 'Maintenance', path: '/billing/maintenance' },
        { key: 'DocumentTracking', menuItemName: 'Document Tracking', path: '/billing/document-tracking' },
        { key: 'ChangeAddress', menuItemName: 'Change Address', path: '/billing/change-address' },
        { key: 'StudentHolds', menuItemName: 'Holds', path: '/billing/holds' },
        { key: 'StudentNotes', menuItemName: 'Student Notes', path: '/billing/student-notes' },
        { key: 'CashierEntry', menuItemName: 'Cashier Entry', path: '/billing/cashier-entry' },
        { key: 'PaymentPlans', menuItemName: 'Payment Plans', path: '/billing/payment-plans/maintenance' },
        { key: 'Authorize', menuItemName: 'Authorize', path: '/billing/authorize' }
    ],
    'Financial Aid': [
        { key: 'Maintenance', menuItemName: 'Maintenance', path: '/financial-aid/maintenance' },
        { key: 'StudentHolds', menuItemName: 'Holds', path: '/financial-aid/holds' },
        { key: 'DocumentTracking', menuItemName: 'Document Tracking', path: '/financial-aid/document-tracking' },
        { key: 'StudentStatus', menuItemName: 'Student Status', path: '/financial-aid/student-status' },
        { key: 'StudentNotes', menuItemName: 'Student Notes', path: '/financial-aid/student-notes' },
        { key: 'WorkStudy', menuItemName: 'Work Study', path: '/financial-aid/work-study/maintenance' },
        { key: 'Transfer', menuItemName: 'Transfer', path: '/financial-aid/transfer' }
    ],
    'Student Life': [
        { key: 'StudentStatus', menuItemName: 'Student Life-Student Life Status', displayName: 'Student Life Status', path: '/student-life/student-status' },
        { key: 'Immunizations', menuItemName: 'Student Life-Immunizations-Manage', displayName: 'Immunizations', path: '/student-life/health-record/immunizations/edit/{studentUID}' },
        { key: 'Housing', menuItemName: 'Student Life-Housing', displayName: 'Housing', path: '/student-life/housing/assign-room' },
        { key: 'MealPlans', menuItemName: 'Student Life-Meal Plans', displayName: 'Meal Plans', path: '/student-life/meal-plans/assign-meal-plans' }
    ]
};

const APPLICANT_LINKS = {
    Admissions: [
        { key: 'ApplicantEvaluation', menuItemName: 'Applicant', displayName: 'Evaluations > Applicant', path: '/admissions/students/evaluations/applicant' },
        { key: 'ChangeAddress', menuItemName: 'Change Address', path: '/admission/students/change-address' },
        { key: 'StudentNotes', menuItemName: 'Student Notes', path: '/admissions/students/student-notes' },
        { key: 'DocumentTracking', menuItemName: 'Document Tracking', path: '/admissions/students/document-tracking' }
    ]
};

// WI 303847 — single Faculty destination: Faculty Maintenance, the faculty
// detail page. The Roster and Grade Entry links seeded by WI 291704 were never
// reachable (faculty only entered quick search with this ticket) and are not in
// this ticket's acceptance criteria, so they are dropped. menuItemName must match
// the user's access tree under "CAMS Manager" (moduleorder 13); MODULE_DISPLAY_NAMES
// carries the AC's "System Manager" label.
const FACULTY_LINKS = {
    'CAMS Manager': [
        { key: 'FacultyMaintenance', menuItemName: 'Faculty Maintenance', path: '/system-manager/faculty/add-faculty' }
    ]
};

// WI 300470 — exactly the five prospect destinations from the acceptance criteria,
// in order: Prospects, International, Citizenship, Prospect Notes, Document Tracking.
// menuItemName must match the user's access tree so buildPermissionIndex can gate
// each link; displayName carries the AC's shorter label where it differs.
const PROSPECT_LINKS = {
    Admissions: [
        { key: 'Prospects', menuItemName: 'Change Prospect', displayName: 'Prospects', path: '/admissions/prospects/add-prospects' },
        { key: 'ProspectInternational', menuItemName: 'Prospect International', displayName: 'International', path: '/admissions/prospects/prospect-international' },
        { key: 'ProspectCitizenship', menuItemName: 'Prospect Citizenship', displayName: 'Citizenship', path: '/admissions/prospects/prospect-citizenship' },
        { key: 'ProspectNotes', menuItemName: 'Prospect Notes', path: '/admissions/prospects/prospect-notes' },
        { key: 'DocumentTracking', menuItemName: 'Document Tracking', path: '/admissions/prospects/document-tracking' }
    ]
};

// WI 300841 — single Organization destination: Admissions > Organizations (Edit).
// menuItemName must match the user's access tree ("Organizations", trimmed — the
// CAMSMenu item is "   Organizations", plural) so buildPermissionIndex can gate the
// link; displayName carries the AC's "Edit" label. Path lands on the search page,
// which auto-opens the record from the deep-link state (WI 300843).
const ORGANIZATION_LINKS = {
    Admissions: [
        { key: 'Organizations', menuItemName: 'Organizations', displayName: 'Edit', path: '/admission/organization' }
    ]
};

// WI 300844 — single Contact destination: Admissions > Contacts (Edit).
// menuItemName must match the user's access tree ("Contacts", trimmed) so
// buildPermissionIndex can gate the link; displayName carries the AC's "Edit" label.
const CONTACT_LINKS = {
    Admissions: [
        { key: 'Contacts', menuItemName: 'Contacts', displayName: 'Edit', path: '/admission/contacts' }
    ]
};

// WI 303833 — single Meal Plan destination: the Meal Plans search page, which
// auto-opens the record in Edit from the deep-link state (see search-meal-plan.js).
// menuItemName must match the user's access tree under Student Life (moduleorder 12):
// "Student Life-Meal Plans" is the page's menu item, the same name STUDENT_LINKS
// already gates on; displayName carries the AC's "Edit" label.
const MEAL_PLAN_LINKS = {
    'Student Life': [
        { key: 'MealPlans', menuItemName: 'Student Life-Meal Plans', displayName: 'Edit', path: '/student-life/meal-plans' }
    ]
};

// WI 303823 — single Housing Period destination: the Housing Periods page, which
// auto-opens the record in the Add/Update editor from the deep-link state (see
// HousingPeriods.js). menuItemName must match the user's access tree under
// Student Life (moduleorder 12): "Student Life-Housing Periods-Manage" is the
// page's menu item (the same name selectHousingPeriodPermissions gates on);
// displayName carries the AC's "Edit" label.
const HOUSING_PERIOD_LINKS = {
    'Student Life': [
        { key: 'HousingPeriods', menuItemName: 'Student Life-Housing Periods-Manage', displayName: 'Edit', path: '/student-life/housing/housing-periods' }
    ]
};

// WI 303828 — single Property destination: the Properties page, which auto-opens
// the record in the Edit wizard from the deep-link state (see search-properties.js).
// menuItemName must match the user's access tree under Student Life (moduleorder 12):
// "Student Life-Properties-Manage" is the page's menu item (the same name the
// manageProperties constant in helper/constants.js gates the page with);
// displayName carries the AC's "Edit" label.
const PROPERTY_LINKS = {
    'Student Life': [
        { key: 'Properties', menuItemName: 'Student Life-Properties-Manage', displayName: 'Edit', path: '/student-life/housing/properties' }
    ]
};

// WI 303843 — the two User destinations enumerated by 303844: "User" (the record itself,
// WI 303849) and "User Access" (its permissions, WI 303845). Both are tabs of the SAME
// route — there is no separate route for the Access tab — so `?tab=access` is what tells
// the two links apart; the deep-link gate reattaches the query string to the target path,
// and syncNavToPath matches on pathname alone, so the nav highlight is unaffected.
// menuItemName must match the user's access tree under "CAMS Manager" (moduleorder 13):
// "User Administration" is the page's menu item — the same name the `User` constant in
// helper/constants.js gates the page with. It is the page's only permission row, so both
// links intentionally gate on it: the access tree draws no distinction between the tabs.
const USER_LINKS = {
    'CAMS Manager': [
        { key: 'UserAdministration', menuItemName: 'User Administration', displayName: 'User', path: '/system-manager/users/user-adminstration' },
        { key: 'UserAccess', menuItemName: 'User Administration', displayName: 'User Access', path: '/system-manager/users/user-adminstration?tab=access' }
    ]
};

// WI 305242 — one destination for both halves of Table Maintenance: the page itself,
// which the deep-link state opens with the row already selected and its options loaded
// (see glossary-table.js / reference-tables.js). That IS the ticket's "Manage Options".
// The two entity types differ only in which tab the link opens: the Reference Tables tab
// is the page's second tab, addressed with ?tab=reference — the same technique USER_LINKS
// uses for its Access tab, and for the same reason (one route, two views). The deep-link
// gate reattaches the query string to the target path, and syncNavToPath matches on
// pathname alone, so the nav highlight is unaffected.
// menuItemName must match the user's access tree under "CAMS Manager" (moduleorder 13):
// "Table Maintenance" is the page's only permission row — the same name the `tablemainten`
// constant in helper/constants.js gates both tabs with — so both links gate on it.
const GLOSSARY_TABLE_LINKS = {
    'CAMS Manager': [
        {
            key: 'GlossaryTableManageOptions',
            menuItemName: 'Table Maintenance',
            displayName: 'Manage Options',
            path: '/system-manager/lookup-table-options/table-maintanance'
        }
    ]
};

const REFERENCE_TABLE_LINKS = {
    'CAMS Manager': [
        {
            key: 'ReferenceTableManageOptions',
            menuItemName: 'Table Maintenance',
            displayName: 'Manage Options',
            path: '/system-manager/lookup-table-options/table-maintanance?tab=reference'
        }
    ]
};

const MODULE_LINKS = {
    student: STUDENT_LINKS,
    applicant: APPLICANT_LINKS,
    faculty: FACULTY_LINKS,
    prospect: PROSPECT_LINKS,
    organization: ORGANIZATION_LINKS,
    contact: CONTACT_LINKS,
    mealplan: MEAL_PLAN_LINKS,
    housingperiod: HOUSING_PERIOD_LINKS,
    property: PROPERTY_LINKS,
    user: USER_LINKS,
    glossarytable: GLOSSARY_TABLE_LINKS,
    referencetable: REFERENCE_TABLE_LINKS
};

export default MODULE_LINKS;
